import { EmptyState } from "@stacklet/ui";
import { graphql, useFragment } from "react-relay";

import { CloudOrganizationCard } from "./CloudOrganizationCard";
import { SamlProviderCard } from "./SamlProviderCard";

import type { IdentityProviders_query$key } from "./__generated__/IdentityProviders_query.graphql";

/** Props for the IdentityProviders component */
interface Props {
  /** Fragment reference for the query the provider list is read from */
  queryRef: IdentityProviders_query$key;
}

/**
 * The identity providers configured for the site, one card per provider.
 *
 * Two kinds are listed together: the SAML providers that federate console
 * logins, and the cloud organizations users and groups are synced from. They
 * are separate roots in the API — `samlProviders` is a plain list, so the whole
 * set loads with the page, while organizations come back as a connection — so
 * they are fetched separately and rendered in turn, SAML providers first.
 * Within the organizations, enabled providers precede the ones still waiting
 * to be switched on.
 *
 * Each root is already typed as its concrete provider, so each gets the card
 * that reads that type rather than one card asking which kind it was handed.
 */
export function IdentityProviders({ queryRef }: Props) {
  const data = useFragment(
    graphql`
      fragment IdentityProviders_query on Query {
        samlProviders {
          id
          ...SamlProviderCard_provider
        }
        # Organizations are few — one per connected cloud account root — so the
        # first page is the whole list in practice.
        cloudOrganizations(first: 100) {
          edges {
            node {
              id
              syncOptions {
                syncGroups
                syncResourceViewerRoles
              }
              ...CloudOrganizationCard_organization
            }
          }
        }
      }
    `,
    queryRef,
  );

  const organizations =
    data.cloudOrganizations.edges?.map((edge) => edge.node) ?? [];

  // Enabled organizations lead; one syncing nothing is not acting as an
  // identity provider yet, so its card (and its Enable call to action) trails
  // the ones already in use. The sort is stable, so API order holds otherwise.
  const byEnabledFirst = [...organizations].sort((a, b) => {
    const enabled = (organization: (typeof organizations)[number]) =>
      organization.syncOptions.syncGroups ||
      organization.syncOptions.syncResourceViewerRoles
        ? 0
        : 1;
    return enabled(a) - enabled(b);
  });

  if (data.samlProviders.length === 0 && organizations.length === 0) {
    return (
      <EmptyState message="No identity providers are configured for this site." />
    );
  }

  return (
    <div className="flex flex-col gap-y-md">
      {data.samlProviders.map((provider) => (
        <SamlProviderCard key={provider.id} providerRef={provider} />
      ))}
      {byEnabledFirst.map((organization) => (
        <CloudOrganizationCard
          key={organization.id}
          organizationRef={organization}
        />
      ))}
    </div>
  );
}
